import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { TokenStore } from './token-store';
import { AuthSessionService } from './auth-session.service';

/**
 * Cross-cutting auth concerns for every HTTP call in the admin panel (Token
 * -> JWT migration).
 *
 * Deliberately does NOT set the Authorization header itself - every service
 * in this app already builds its own via ApiUrl.getJWTToken(), which now
 * returns `Bearer <jwt>` once one exists (falling back to the legacy
 * `Token <token>` otherwise). Rewriting every call site to stop doing that
 * was the bigger, riskier change; fixing the one function they all already
 * call was not. This interceptor only overrides Authorization on the 401
 * retry below, where it must use a token that did not exist when the
 * original request was built.
 *
 * `withCredentials` is unconditional, not gated by URL. That gating (only
 * `/api/auth/` needs it) is exactly what caused a real production incident
 * on font-end this migration is built to not repeat: the LOGIN response is
 * the first thing that tries to set the `sale4all_refresh` cookie, and the
 * login endpoint (`/api-token-auth/`) does not live under `/api/auth/` at
 * all - a URL allowlist is always one endpoint away from missing that.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  // Lazily resolved: AuthSessionService depends on HttpClient, and
  // HttpClient depends on the interceptor chain - a constructor dependency
  // here would be a cycle Angular cannot resolve.
  private get auth(): AuthSessionService {
    return this.injector.get(AuthSessionService);
  }

  private getCookie(name: string): string | null {
    if (typeof document === 'undefined' || !document.cookie) {
      return null;
    }
    const matches = document.cookie.match(new RegExp(
      '(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'
    ));
    return matches ? decodeURIComponent(matches[1]) : null;
  }

  private isAuthEndpoint(url: string): boolean {
    return url.indexOf('/api/auth/') !== -1;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let headers = req.headers;

    const csrfToken = this.getCookie('csrftoken');
    if (csrfToken && ['GET', 'HEAD'].indexOf(req.method) === -1) {
      headers = headers.set('X-CSRFToken', csrfToken);
    }

    const modifiedReq = req.clone({ headers, withCredentials: true });

    return next.handle(modifiedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // A 401 on an ordinary call means the JWT access token expired (a
        // legacy DRF token never expires, so an admin still on one never
        // sees this path at all). Refresh once and replay so they never see
        // it either. Never do this for /api/auth/ itself: a failing refresh
        // must not recurse into another refresh.
        if (
          error.status === 401 &&
          !this.isAuthEndpoint(req.url) &&
          TokenStore.isLoggedIn()
        ) {
          return this.auth.refresh().pipe(
            switchMap((newAccess: string | null) => {
              if (!newAccess) {
                return throwError(error);
              }
              const retried = req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + newAccess),
                withCredentials: true
              });
              return next.handle(retried);
            }),
            catchError(refreshError => {
              // Includes TOKEN_REUSE_DETECTED - the session was revoked for
              // security reasons and is not retryable.
              TokenStore.clear();
              return throwError(refreshError);
            })
          );
        }
        return throwError(error);
      })
    );
  }
}
